import {
  courseSchema,
  skillSchema,
  unitSchema,
  type Course,
  type Lesson,
  type Skill,
  type Unit,
} from '@fluent/types';
import { arePhrasesLessonsLoaded, getLesson } from './lessons.js';

/**
 * Course / unit / skill loaders. Parallel to `lessons.ts` — eagerly loads
 * every course.json / unit.json / skill.json via Vite's glob at build time,
 * validates each with its Zod schema, and exposes lookups by id. This
 * includes the phrases course: its structure is a few KB and stays eager —
 * only its LESSONS are lazy (lessons.ts).
 *
 * Tree helpers (`getCourseTree`, `findLessonLocation`) compose the three
 * stores into a navigable structure for the home skill tree.
 * `getCourseOutline` / `courseOfLesson` need ids only, so they work for the
 * phrases course before its lessons are loaded.
 */

const courseModules = import.meta.glob<unknown>(
  '../../../../content/courses/**/course.json',
  { eager: true, import: 'default' },
);
const unitModules = import.meta.glob<unknown>(
  '../../../../content/courses/**/unit.json',
  { eager: true, import: 'default' },
);
const skillModules = import.meta.glob<unknown>(
  '../../../../content/courses/**/skill.json',
  { eager: true, import: 'default' },
);

function buildIndex<T extends { id: string }>(
  modules: Record<string, unknown>,
  parse: (raw: unknown) => { success: true; data: T } | { success: false; error: unknown },
  kind: string,
): Map<string, T> {
  const index = new Map<string, T>();
  for (const [path, raw] of Object.entries(modules)) {
    const parsed = parse(raw);
    if (!parsed.success) {
      throw new Error(`Invalid ${kind} JSON at ${path}: ${String(parsed.error)}`);
    }
    const value = parsed.data;
    if (index.has(value.id)) {
      throw new Error(`Duplicate ${kind} id "${value.id}" (at ${path})`);
    }
    index.set(value.id, value);
  }
  return index;
}

const coursesById = buildIndex(courseModules, (r) => courseSchema.safeParse(r), 'course');
const unitsById = buildIndex(unitModules, (r) => unitSchema.safeParse(r), 'unit');
const skillsById = buildIndex(skillModules, (r) => skillSchema.safeParse(r), 'skill');

export function getCourse(id: string): Course | null {
  return coursesById.get(id) ?? null;
}

export function getUnit(id: string): Unit | null {
  return unitsById.get(id) ?? null;
}

export function getSkill(id: string): Skill | null {
  return skillsById.get(id) ?? null;
}

/** The single course id in the repo right now. Hardcoded deliberately —
 *  once we introduce more courses (e.g. Russian→English) we swap this
 *  for a per-user preference. */
export const DEFAULT_COURSE_ID = 'ka-en';

/** The phrases course (ფრაზები, 2026-10-18 — PHRASES.md): a separate track
 *  with its own tab and screen. Its lessons share the global lesson index
 *  (so `/learn/:id` plays them) once loaded — they are fetched lazily
 *  (lessons.ts `loadPhrasesLessons`) — but never join the main skill tree,
 *  the typo matcher's known words, the flashcards deck or the admin paywall
 *  funnel. */
export const PHRASES_COURSE_ID = 'ka-en-phrases';

/** Are this course's lessons in the index right now? Always for the main
 *  course (bundled eagerly); for the phrases course only once loaded. */
function courseLessonsLoaded(courseId: string): boolean {
  return courseId !== PHRASES_COURSE_ID || arePhrasesLessonsLoaded();
}

/* ---------------------------------------------------------------------- */
/*  Tree helpers                                                          */
/* ---------------------------------------------------------------------- */

export interface CourseTreeUnit extends Unit {
  skills: Skill['id'][]; // preserve original ids
  resolvedSkills: CourseTreeSkill[];
}

export interface CourseTreeSkill extends Skill {
  resolvedLessons: Lesson[];
}

export interface CourseTree {
  course: Course;
  units: CourseTreeUnit[];
  /** Flat, ordered lesson ids for progression (unit→skill→lesson order). */
  orderedLessonIds: string[];
}

/**
 * The course with every unit, skill and lesson resolved. Null when the
 * course does not exist — or, for the phrases course, while its lessons are
 * not loaded yet (`loadPhrasesLessons()` / `usePhrasesContent()` first).
 * Throws if the course references a unit, skill or lesson that is missing.
 */
export function getCourseTree(courseId: string = DEFAULT_COURSE_ID): CourseTree | null {
  const course = getCourse(courseId);
  if (!course) return null;
  if (!courseLessonsLoaded(courseId)) return null;

  const units: CourseTreeUnit[] = [];
  const orderedLessonIds: string[] = [];

  for (const unitId of course.units) {
    const unit = getUnit(unitId);
    if (!unit) {
      throw new Error(`Course "${courseId}" references missing unit "${unitId}".`);
    }
    const resolvedSkills: CourseTreeSkill[] = [];
    for (const skillId of unit.skills) {
      const skill = getSkill(skillId);
      if (!skill) {
        throw new Error(`Unit "${unitId}" references missing skill "${skillId}".`);
      }
      const resolvedLessons: Lesson[] = [];
      for (const lessonId of skill.lessons) {
        const lesson = getLesson(lessonId);
        if (!lesson) {
          throw new Error(`Skill "${skillId}" references missing lesson "${lessonId}".`);
        }
        resolvedLessons.push(lesson);
        orderedLessonIds.push(lessonId);
      }
      resolvedSkills.push({ ...skill, resolvedLessons });
    }
    units.push({ ...unit, skills: unit.skills, resolvedSkills });
  }

  return { course, units, orderedLessonIds };
}

/** Look up where a lesson sits in the tree — useful for progression checks. */
export function findLessonLocation(
  lessonId: string,
  courseId: string = DEFAULT_COURSE_ID,
): { unitId: string; skillId: string; indexInSkill: number; indexInCourse: number } | null {
  const tree = getCourseTree(courseId);
  if (!tree) return null;
  let indexInCourse = 0;
  for (const unit of tree.units) {
    for (const skill of unit.resolvedSkills) {
      const indexInSkill = skill.lessons.indexOf(lessonId);
      if (indexInSkill >= 0) {
        return {
          unitId: unit.id,
          skillId: skill.id,
          indexInSkill,
          indexInCourse: indexInCourse + indexInSkill,
        };
      }
      indexInCourse += skill.lessons.length;
    }
  }
  return null;
}

/* ---------------------------------------------------------------------- */
/*  Outlines — ids only, no lesson content                                */
/* ---------------------------------------------------------------------- */

/** A course's structure with ids only, read from course / unit / skill
 *  JSON — no lesson content needed, so it is there for the phrases course
 *  before its lessons are loaded. Same shape as `CourseTree` (minus the
 *  lesson bodies), so helpers that read only ids from a tree — e.g.
 *  `buildFreeAccess` — accept either. */
export interface CourseOutline {
  course: Course;
  units: Array<{
    id: string;
    resolvedSkills: Array<{ id: string; resolvedLessons: Array<{ id: string }> }>;
  }>;
  /** Flat, ordered lesson ids (unit→skill→lesson order). */
  orderedLessonIds: string[];
}

/** The course's outline (ids only), or null if the course does not exist.
 *  Throws on a reference to a missing unit or skill, like `getCourseTree`. */
export function getCourseOutline(courseId: string = DEFAULT_COURSE_ID): CourseOutline | null {
  const course = getCourse(courseId);
  if (!course) return null;
  const orderedLessonIds: string[] = [];
  const units = course.units.map((unitId) => {
    const unit = getUnit(unitId);
    if (!unit) {
      throw new Error(`Course "${courseId}" references missing unit "${unitId}".`);
    }
    const resolvedSkills = unit.skills.map((skillId) => {
      const skill = getSkill(skillId);
      if (!skill) {
        throw new Error(`Unit "${unitId}" references missing skill "${skillId}".`);
      }
      orderedLessonIds.push(...skill.lessons);
      return { id: skill.id, resolvedLessons: skill.lessons.map((id) => ({ id })) };
    });
    return { id: unit.id, resolvedSkills };
  });
  return { course, units, orderedLessonIds };
}

/* ---------------------------------------------------------------------- */
/*  Phrases course (ფრაზები, 2026-10-18 — PHRASES.md)                      */
/* ---------------------------------------------------------------------- */

let courseOfLessonCache: Map<string, string> | null = null;

/** Which course a lesson belongs to (`null` = in no course tree). Built from
 *  the outlines (skill.json lesson lists), so it never needs lesson content
 *  — a phrases lesson is known as one before its lessons are loaded. */
export function courseOfLesson(lessonId: string): string | null {
  if (!courseOfLessonCache) {
    courseOfLessonCache = new Map();
    for (const courseId of coursesById.keys()) {
      for (const id of getCourseOutline(courseId)?.orderedLessonIds ?? []) {
        courseOfLessonCache.set(id, courseId);
      }
    }
  }
  return courseOfLessonCache.get(lessonId) ?? null;
}

/** True for lessons of the phrases course. Pure id check first (phrases
 *  lessons use the reserved 1101–1699 number block — PHRASES.md § 2), so it
 *  also works for ids read from the database that no skill.json lists. */
export function isPhrasesLessonId(lessonId: string): boolean {
  return /^lesson-1[1-6]\d\d-/u.test(lessonId) || courseOfLesson(lessonId) === PHRASES_COURSE_ID;
}

/** True for lessons of the main (skill-tree) course. */
export function isMainCourseLessonId(lessonId: string): boolean {
  return !isPhrasesLessonId(lessonId);
}
